import { CellValueChangedEvent, ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Box } from "@chakra-ui/react";
import { useSidebar } from "../../../providers/Sidebar";
import { ExtractionDataItem, TableDataItem, TemplateField } from "../../../api/types";
import { useUpdateExtractionField } from "../../../api/useUpdateExtractionField";
import useThemeStore from "../../../stores/useThemeStore";
import classes from "../../../utils/classes";
import { typedSxMap } from "../../../utils/typedSxMap";
import "./../style/TableStyle.scss";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { CELL_COLOR_MAP, cellRenderer } from "./cellRenderer";

type PDFTableFieldViewerProps = {
  extractionId?: string;
  selectedDataItem: ExtractionDataItem | null;
  template: TemplateField[];
  selectedTemplateKey: string | null;
  isEditable?: boolean;
  onUpdateDataItem?: (item: ExtractionDataItem, key: string) => void;
  isDemo?: boolean; // Hacky, replace this with a better solution
};

export default function PDFTableFieldViewer({
  selectedDataItem,
  template,
  selectedTemplateKey,
  extractionId,
  isEditable,
  onUpdateDataItem,
  isDemo,
}: PDFTableFieldViewerProps) {
  const theme = useThemeStore((state) => state.theme);
  const { navSize } = useSidebar();
  const templateColumns: TemplateField[] = (selectedTemplateKey ? template.find(({ key }) => key === selectedTemplateKey)?.columns : []) || [];
  const columns: ColDef[] = templateColumns?.map(({ key, name, data_type, validations }) => ({
    field: `${key}.value`,
    headerName: name,
    flex: data_type === "number" ? 1 : 2,
    minWidth: data_type === "number" ? 100 : 200,
    editable: isEditable,
    cellDataType: "string",

    cellRenderer: (params: ICellRendererParams) => cellRenderer(params, key, theme, templateColumns),
    cellStyle: (params: any) => {
      const errors = params?.data?.[key]?.errors;
      const validation = templateColumns.find((column) => column.key === key)?.validations.find((validation) => validation.id === errors?.[0]);

      if (errors) {
        return {
          backgroundColor: CELL_COLOR_MAP[validation?.severity as keyof typeof CELL_COLOR_MAP] || "transparent",
        };
      }
      return { backgroundColor: "" };
    },
  }));
  const rowData = selectedDataItem?.table_data || [];

  const updateExtractionField = useUpdateExtractionField();

  const editCell = async (event: CellValueChangedEvent<TableDataItem>) => {
    const colId = event?.column.getColId();
    const key = colId.replace(".value", "");
    const value = event?.newValue;
    const rowIndex = event.rowIndex;
    if (!extractionId || !key || selectedTemplateKey === null || rowIndex === null) return;

    try {
      const updatedItem = await updateExtractionField.mutateAsync({
        extractionId,
        key,
        value,
        tableKey: selectedTemplateKey,
        tableRowIndex: rowIndex,
      });

      onUpdateDataItem && onUpdateDataItem(updatedItem, key);
      const newRowData: { [key: string]: any } = event.data;
      newRowData[key] = updatedItem;
      event.node.updateData(newRowData as any);
    } catch (error) {
      console.error("Failed to update extraction field:", error);
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    const allColumns = params.api.getColumns();
    if (allColumns && allColumns.length > 0) {
      // Adjust the width of the last column to hide the lines on the side
      const lastColumn = allColumns[allColumns.length - 1];
      if (lastColumn) {
        const newWidth = lastColumn.getActualWidth() + 2;
        params.api.setColumnWidths([{ key: lastColumn.getColId(), newWidth: newWidth }]);
      }
    }
  };

  const styles = typedSxMap({
    container: {
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      width: "100%",
      height: 200,
      zIndex: 1000,
    },
    tableWrapper: {
      boxShadow: "0 -8px 20px -5px rgba(0, 0, 0, 0.2)",
      height: "100%",
      marginLeft: isDemo ? "246px" : navSize === "small" ? "110px" : "285px",
      marginRight: "47px",
      "& .ag-root-wrapper": {
        borderRadius: "8px 8px 0 0",
        overflow: "hidden",
      },
      "& .ag-header": {
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
      },
      "& .ag-body-viewport": {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
  });

  return (
    columns.length > 0 && (
      <Box sx={styles.container}>
        <Box sx={styles.tableWrapper} className={classes([theme === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"])}>
          <AgGridReact
            columnDefs={columns}
            rowData={rowData}
            onCellValueChanged={editCell}
            onGridReady={onGridReady}
            domLayout="normal"
            suppressColumnVirtualisation={true}
          />
        </Box>
      </Box>
    )
  );
}
